<template>
  <div class="flex items-center h-full w-full relative">
    <span class="text-sm leading-normal flex items-center gap-1">
      <template v-if="isLoading && showLoadingIndicator">
        loading...
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><!-- Icon from Material Line Icons by Vjacheslav Trushkin - https://github.com/cyberalien/line-md/blob/master/license.txt --><path fill="none" stroke="currentColor" stroke-dasharray="16" stroke-dashoffset="16" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3c4.97 0 9 4.03 9 9"><animate fill="freeze" attributeName="stroke-dashoffset" dur="0.2s" values="16;0"/><animateTransform attributeName="transform" dur="1.5s" repeatCount="indefinite" type="rotate" values="0 12 12;360 12 12"/></path></svg>
        
      </template>
      <template v-else>
        {{ formattedValue }}
      </template>
    </span>
    <div v-if="hasApiData" class="absolute top-0.5 right-0.5 text-primary/70 hover:text-primary text-xs pointer-events-none">
      🛈
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  params: any
}

const props = defineProps<Props>()

// Add error handling for params access
const safeParams = computed(() => props.params || {})

// Check if loading indicators should be shown for this column
const showLoadingIndicator = computed(() => {
  try {
    const colDef = safeParams.value.colDef
    // Check if the column definition has showLoadingIndicator set to true
    return colDef?.showLoadingIndicator === true
  } catch (error) {
    console.warn('MeasurementCell showLoadingIndicator error:', error)
    return false
  }
})

// Check if this cell is currently loading data
const isLoading = computed(() => {
  try {
    const gridContext = safeParams.value.context
    const field = safeParams.value.colDef?.field
    const rowIndex = safeParams.value.node?.rowIndex
    
    // Check if this specific cell is loading from autocomplete or transformers
    if (gridContext?.autocompleteLoadingCells && field && rowIndex !== undefined) {
      const cellId = `${rowIndex}-${field}`
      return gridContext.autocompleteLoadingCells.has(cellId)
    }
    
    // Also check if the value indicates it's loading/pending
    const value = safeParams.value.value
    if (typeof value === 'object' && value !== null) {
      return ('loading' in value && value.loading === true) || 
             ('status' in value && (value.status === 'loading' || value.status === 'pending'))
    }
    
    return false
  } catch (error) {
    console.warn('MeasurementCell isLoading error:', error)
    return false
  }
})

const formattedValue = computed(() => {
  try {
    const value = safeParams.value.value
    
    // DEBUG: Log what the MeasurementCell renderer is receiving
    // console.log('[MeasurementCell DEBUG] Received params:', {
    //   value: value,
    //   valueType: typeof value,
    //   hasValue: value !== null && value !== undefined,
    //   rowIndex: safeParams.value.rowIndex,
    //   field: safeParams.value.colDef?.field
    // })
    
    if (value === null || value === undefined) {
      // console.log('[MeasurementCell DEBUG] Value is null/undefined, returning empty')
      return ''
    }
    
    const effectiveValue = typeof value === 'object' && value !== null
      ? value.value
      : value
    
    // console.log('[MeasurementCell DEBUG] Effective value:', effectiveValue)
      
    if (effectiveValue === null || effectiveValue === undefined || effectiveValue === '') {
      // console.log('[MeasurementCell DEBUG] Effective value is empty, returning empty string')
      return ''
    }
    
    const numValue = Number(effectiveValue)
    const result = !isNaN(numValue) ? `${numValue.toFixed(1)}m` : String(effectiveValue)
    // console.log('[MeasurementCell DEBUG] Final formatted value:', result)
    return result
  } catch (error) {
    console.warn('MeasurementCell formattedValue error:', error)
    return ''
  }
})

const hasApiData = computed(() => {
  try {
    const value = safeParams.value.value
    return typeof value === 'object' && value !== null && 
      (value.sourceName || value.groundingMetadata || value.sourceText)
  } catch (error) {
    console.warn('MeasurementCell hasApiData error:', error)
    return false
  }
})
</script> 